import { call, put, takeLatest, fork } from 'redux-saga/effects'
import { createApiCall } from 'services'

import { setOwners, setOwner } from './actions'
import {
  ActionType,
  type GetOwnersResponse,
  type GetOwnersPayload,
  type GetOwnerByIdPayload,
  type GetOwnerByIdResponse,
  type UpdateOwnerPayload,
  type UpdateOwnerResponse,
  type DeleteOwnerPayload,
  type DeleteOwnerResponse,
  type BulkDeleteOwnersPayload,
  type BulkDeleteOwnersResponse,
  type ArchiveOwnerPayload,
  type ArchiveOwnerResponse,
} from './types'
import { ActionCallback } from 'store/model'

export function* getOwnersSaga(): Generator {
  yield takeLatest(ActionType.GET_OWNERS, function* (action: ActionCallback<GetOwnersPayload>) {
    const { payload, callback } = action

    try {
      const response: GetOwnersResponse = yield call(createApiCall, {
        method: 'GET',
        url: `/owners`,
        params: payload,
      })

      const { status, data } = response

      if (status === 200) {
        yield put(setOwners(data))
      }

      if (callback) callback()
    } catch (error) {
      console.error('Failed to fetch owners:', error)
      yield put(setOwners(null))
    }
  })
}

export function* getOwnerByIdSaga(): Generator {
  yield takeLatest(ActionType.GET_OWNER, function* (action: ActionCallback<GetOwnerByIdPayload>) {
    const { payload, callback } = action
    const { ownerId, limit = 10, offset = 0 } = payload

    try {
      const response: GetOwnerByIdResponse = yield call(createApiCall, {
        method: 'GET',
        url: `/owners/${ownerId}`,
        params: { limit, offset },
      })

      const { status, data } = response

      if (status === 200) {
        yield put(setOwner(data))
      }

      if (callback) callback({ success: true, data })
    } catch (error) {
      console.error('Failed to fetch owner:', error)
      yield put(setOwner(null))
      if (callback) callback({ success: false, error })
    }
  })
}

export function* updateOwnerSaga(): Generator {
  yield takeLatest(ActionType.UPDATE_OWNER, function* (action: ActionCallback<UpdateOwnerPayload>) {
    const { payload, callback } = action
    const { ownerId, ...updateData } = payload

    try {
      const response: { status: number; data: UpdateOwnerResponse } = yield call(createApiCall, {
        method: 'PUT',
        url: `/owners/${ownerId}`,
        data: updateData,
      })

      const { status, data } = response

      if (status === 200) {
        // Optionally refresh the owner data after update
        // yield put(getOwnerById({ ownerId }))
      }

      if (callback) callback({ success: true, data })
    } catch (error) {
      console.error('Failed to update owner:', error)
      if (callback) callback({ success: false, error })
    }
  })
}

export function* deleteOwnerSaga(): Generator {
  yield takeLatest(ActionType.DELETE_OWNER, function* (action: ActionCallback<DeleteOwnerPayload>) {
    const { payload, callback } = action

    try {
      const response: { status: number; data: DeleteOwnerResponse } = yield call(createApiCall, {
        method: 'DELETE',
        url: `/owners/${payload.ownerId}`,
      })

      const { status, data } = response

      if (status === 200) {
        // Success handled in component via callback
      }

      if (callback) callback({ success: true, data })
    } catch (error) {
      console.error('Failed to delete owner:', error)
      if (callback) callback({ success: false, error })
    }
  })
}

export function* bulkDeleteOwnersSaga(): Generator {
  yield takeLatest(ActionType.BULK_DELETE_OWNERS, function* (action: ActionCallback<BulkDeleteOwnersPayload>) {
    const { payload, callback } = action

    try {
      const response: { status: number; data: BulkDeleteOwnersResponse } = yield call(createApiCall, {
        method: 'POST',
        url: `/owners/bulk-delete`,
        data: payload,
      })

      const { status, data } = response

      if (status === 200) {
        // Success handled in component via callback
      }

      if (callback) callback({ success: true, data })
    } catch (error) {
      console.error('Failed to bulk delete owners:', error)
      if (callback) callback({ success: false, error })
    }
  })
}

export function* archiveOwnerSaga(): Generator {
  yield takeLatest(ActionType.ARCHIVE_OWNER, function* (action: ActionCallback<ArchiveOwnerPayload>) {
    const { payload, callback } = action

    try {
      const response: { status: number; data: ArchiveOwnerResponse } = yield call(createApiCall, {
        method: 'PATCH',
        url: `/owners/${payload.ownerId}/archive`,
      })

      const { data } = response

      if (callback) callback({ success: true, data })
    } catch (error) {
      console.error('Failed to archive owner:', error)
      if (callback) callback({ success: false, error })
    }
  })
}

export function* unarchiveOwnerSaga(): Generator {
  yield takeLatest(ActionType.UNARCHIVE_OWNER, function* (action: ActionCallback<ArchiveOwnerPayload>) {
    const { payload, callback } = action

    try {
      const response: { status: number; data: ArchiveOwnerResponse } = yield call(createApiCall, {
        method: 'PATCH',
        url: `/owners/${payload.ownerId}/unarchive`,
      })

      const { data } = response

      if (callback) callback({ success: true, data })
    } catch (error) {
      console.error('Failed to unarchive owner:', error)
      if (callback) callback({ success: false, error })
    }
  })
}

export default [
  fork(getOwnersSaga),
  fork(getOwnerByIdSaga),
  fork(updateOwnerSaga),
  fork(deleteOwnerSaga),
  fork(bulkDeleteOwnersSaga),
  fork(archiveOwnerSaga),
  fork(unarchiveOwnerSaga),
]
